/**
 * VoidPaymentDialog (Iter 80)
 *
 * Primary-Admin-only PERMANENT void of a settlement row. Two-step:
 *   Step 1 — operator captures reason / note / optional bank reference
 *            + ticks the impact-acknowledgement checkbox.
 *   Step 2 — server-computed impact preview renders BEFORE commit so
 *            the values shown cannot drift from what the commit writes.
 *            Operator confirms.
 *
 * Backend:
 *   GET   /api/billing/payment/{id}/void/preview  (no mutation)
 *   PATCH /api/billing/payment/{id}/void          (commit)
 *
 * Filing gates surfaced by the server:
 *   - parent invoice GST-Filed → blocks
 *   - payment TDS status Claimed / Adjusted → blocks
 * When blocked, the dialog shows the canonical recovery CTA instead of
 * the confirmation footer.
 *
 * No Unvoid path. Once committed, the operator must record a fresh
 * settlement to undo a mistaken void.
 */
import { useEffect, useMemo, useState } from "react";
import { Prohibit, Warning, ShieldCheck, ArrowRight, ArrowDown } from "@phosphor-icons/react";
import { api, formatApiError } from "../lib/api";
import { formatINR, formatDate } from "../lib/format";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { DialogFooter } from "./ui/dialog";
import FormDialog, { FormDialogCancel } from "./FormDialog";
import { toast } from "sonner";

const REASONS = [
    { value: "Duplicate",      label: "Duplicate — accidental double-capture" },
    { value: "WrongInvoice",   label: "Wrong invoice — receipt applied to a different invoice" },
    { value: "BouncedCheque",  label: "Bounced cheque — bank reversed the receipt" },
    { value: "FraudReversal",  label: "Fraud reversal — receipt undone by external action" },
    { value: "Other",          label: "Other (must be substantively explained in the note)" },
];

const NOTE_MIN = 10;

export default function VoidPaymentDialog({ open, onOpenChange, payment, onDone }) {
    const [step, setStep] = useState(1);
    const [reason, setReason] = useState("");
    const [note, setNote] = useState("");
    const [bankRef, setBankRef] = useState("");
    const [ack, setAck] = useState(false);
    const [busy, setBusy] = useState(false);
    const [preview, setPreview] = useState(null);
    const [previewBusy, setPreviewBusy] = useState(false);
    const [previewErr, setPreviewErr] = useState("");

    // Reset every time we open onto a new payment
    useEffect(() => {
        if (!open) {
            setStep(1); setReason(""); setNote(""); setBankRef("");
            setAck(false); setBusy(false); setPreview(null);
            setPreviewBusy(false); setPreviewErr("");
            return;
        }
    }, [open, payment?.id]);

    // Step 2 → fetch the impact preview once the operator advances.
    useEffect(() => {
        if (step !== 2 || !payment?.id || !open) return;
        let cancelled = false;
        setPreviewBusy(true);
        setPreviewErr("");
        api.get(`/billing/payment/${payment.id}/void/preview`)
            .then((res) => {
                if (cancelled) return;
                setPreview(res.data);
            })
            .catch((err) => {
                if (cancelled) return;
                setPreviewErr(formatApiError(err, "Failed to load impact preview"));
            })
            .finally(() => !cancelled && setPreviewBusy(false));
        return () => { cancelled = true; };
    }, [step, payment?.id, open]);

    const filingBlock = preview?.filing_block || null;
    const isBlocked = !!filingBlock;

    const step1Valid = useMemo(() => (
        !!reason && note.trim().length >= NOTE_MIN
    ), [reason, note]);

    const goToPreview = () => {
        if (!step1Valid) return;
        setStep(2);
    };

    const submit = async () => {
        if (!ack || isBlocked) return;
        setBusy(true);
        try {
            const body = {
                reason, note: note.trim(),
                ...(bankRef.trim() ? { bank_reference: bankRef.trim() } : {}),
            };
            await api.patch(`/billing/payment/${payment.id}/void`, body);
            toast.success(`Payment ${payment.id} voided — outstanding restored`);
            onDone?.();
            onOpenChange?.(false);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to void payment"));
        } finally {
            setBusy(false);
        }
    };

    if (!payment) return null;
    const isDirty = !!reason || note.length > 0 || bankRef.length > 0;

    return (
        <FormDialog
            open={open}
            onOpenChange={(v) => !busy && onOpenChange(v)}
            isDirty={isDirty}
            contentClassName="max-w-2xl"
            testId="void-payment-dialog"
            srTitle="Void payment (permanent reversal)"
            srDescription="Primary-Admin-only permanent void of a settlement. Two-step confirmation with server-computed impact preview. No unvoid path."
            header={
                <div className="space-y-1.5">
                    <div className="flex items-center gap-2 text-lg font-semibold">
                        <Prohibit size={18} weight="duotone" className="text-rose-600" />
                        Void payment
                        <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide bg-rose-50 text-rose-700 border border-rose-200">
                            <ShieldCheck size={9} weight="fill" /> Primary Admin · Permanent
                        </span>
                    </div>
                    <div className="text-xs text-slate-500">
                        Logically reverses this settlement. No unvoid path — record a fresh
                        settlement if you make a mistake. Cannot be applied to GST-filed
                        invoices or TDS-Claimed settlements.
                    </div>
                </div>
            }
        >
            {step === 1 && (
                <div className="space-y-4 py-2" data-testid="void-payment-step1">
                    {/* Existing row context */}
                    <div className="rounded-md border border-slate-200 bg-slate-50/60 px-3 py-2 text-xs text-slate-700 space-y-1">
                        <div className="flex items-center justify-between">
                            <span className="reedos-eyebrow text-[10px]">Settlement to void</span>
                            <span className="text-[10px] text-slate-400 font-mono">{payment.id}</span>
                        </div>
                        <div className="flex items-center gap-3">
                            <span>Cash <b className="text-slate-900">{formatINR(payment.amount)}</b></span>
                            <span>·</span>
                            <span>TDS <b className="text-slate-900">{formatINR(payment.tds_amount || 0)}</b></span>
                            <span>·</span>
                            <span>Date <b className="text-slate-900">{formatDate(payment.payment_date)}</b></span>
                        </div>
                        <div className="text-[10px] text-slate-500">
                            Mode: {payment.payment_mode} · Ref: {payment.reference_number || "—"} · TDS stage: {payment.tds_status || "Deducted"}
                        </div>
                    </div>

                    {/* Reason */}
                    <div>
                        <Label className="text-xs font-semibold">Void reason *</Label>
                        <select
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            className="mt-1 w-full h-9 rounded-md border border-slate-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-rose-400/40"
                            data-testid="void-payment-reason-select"
                        >
                            <option value="">— Pick a reason —</option>
                            {REASONS.map((r) => (
                                <option key={r.value} value={r.value}>{r.label}</option>
                            ))}
                        </select>
                    </div>

                    {/* Note */}
                    <div>
                        <Label className="text-xs font-semibold">Operator note *</Label>
                        <textarea
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder="What happened, why this needs to be voided, supporting reference…"
                            rows={3}
                            maxLength={1000}
                            className="mt-1 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-rose-400/40"
                            data-testid="void-payment-note-input"
                        />
                        <div className="text-[10px] text-slate-400 mt-1">
                            {note.length} / 1000 · min {NOTE_MIN} characters
                        </div>
                    </div>

                    {/* Bank reference (optional) */}
                    <div>
                        <Label className="text-xs font-semibold">Bank statement / Form 16A reference <span className="text-slate-400">(optional)</span></Label>
                        <Input
                            type="text"
                            value={bankRef}
                            onChange={(e) => setBankRef(e.target.value)}
                            placeholder="e.g. HDFC stmt 2026-02-12 line 47"
                            className="mt-1"
                            maxLength={200}
                            data-testid="void-payment-bank-ref-input"
                        />
                    </div>
                </div>
            )}

            {step === 2 && (
                <div className="space-y-4 py-2" data-testid="void-payment-step2">
                    {previewBusy && (
                        <div className="text-center py-8 text-sm text-slate-500" data-testid="void-payment-preview-loading">
                            Computing impact…
                        </div>
                    )}
                    {previewErr && (
                        <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-3 text-sm text-rose-800" data-testid="void-payment-preview-error">
                            {previewErr}
                        </div>
                    )}
                    {preview && !previewBusy && (
                        <>
                            {isBlocked ? (
                                <div className="rounded-md border-2 border-amber-300 bg-amber-50 p-4 space-y-2" data-testid="void-payment-filing-block">
                                    <div className="flex items-center gap-2 text-sm font-semibold text-amber-900">
                                        <Warning size={18} weight="fill" /> Cannot void this settlement
                                    </div>
                                    <div className="text-xs text-amber-900 leading-relaxed">
                                        {filingBlock.detail}
                                    </div>
                                </div>
                            ) : (
                                <div className="rounded-md border-2 border-rose-200 bg-white p-4 space-y-3" data-testid="void-payment-impact-summary">
                                    <div className="text-[10px] reedos-eyebrow text-rose-700">Impact summary — review carefully</div>
                                    <div className="grid grid-cols-2 gap-y-2 text-sm">
                                        <div className="text-slate-500">Invoice</div>
                                        <div className="font-semibold text-slate-900" data-testid="void-preview-invoice-number">
                                            {preview.invoice.invoice_number}
                                            {preview.invoice.client_name && (
                                                <span className="ml-2 text-xs text-slate-500">— {preview.invoice.client_name}</span>
                                            )}
                                        </div>

                                        <div className="text-slate-500">Payment amount</div>
                                        <div className="font-mono font-semibold text-slate-900" data-testid="void-preview-payment-amount">
                                            {formatINR(preview.payment.amount)}
                                            {preview.payment.tds_amount > 0 && (
                                                <span className="ml-2 text-xs text-amber-700">+ TDS {formatINR(preview.payment.tds_amount)}</span>
                                            )}
                                        </div>

                                        <div className="text-slate-500">Payment date</div>
                                        <div className="text-sm text-slate-700">{formatDate(preview.payment.payment_date)}</div>
                                    </div>

                                    <div className="border-t border-slate-100 pt-3 grid grid-cols-3 gap-3">
                                        <div className="rounded border border-slate-200 p-2.5">
                                            <div className="text-[10px] reedos-eyebrow text-slate-500">Status</div>
                                            <div className="mt-1 text-xs font-semibold inline-flex items-center gap-1.5">
                                                <span className="px-1.5 py-0.5 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded" data-testid="void-preview-status-before">{preview.before.status}</span>
                                                <ArrowRight size={10} className="text-slate-400" />
                                                <span className="px-1.5 py-0.5 bg-rose-50 border border-rose-200 text-rose-800 rounded" data-testid="void-preview-status-after">{preview.after.status}</span>
                                            </div>
                                        </div>
                                        <div className="rounded border border-slate-200 p-2.5">
                                            <div className="text-[10px] reedos-eyebrow text-slate-500">Outstanding</div>
                                            <div className="mt-1 text-xs font-mono">
                                                <span data-testid="void-preview-outstanding-before">{formatINR(preview.before.outstanding)}</span>
                                                <ArrowRight size={10} className="inline mx-1 text-slate-400" />
                                                <span className="font-semibold text-rose-700" data-testid="void-preview-outstanding-after">{formatINR(preview.after.outstanding)}</span>
                                            </div>
                                        </div>
                                        <div className="rounded border border-slate-200 p-2.5">
                                            <div className="text-[10px] reedos-eyebrow text-slate-500">Cash collected</div>
                                            <div className="mt-1 text-xs font-mono">
                                                <span>{formatINR(preview.before.paid)}</span>
                                                <ArrowDown size={10} className="inline mx-1 text-slate-400" />
                                                <span className="font-semibold">{formatINR(preview.after.paid)}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="text-[11px] text-slate-500 leading-relaxed">
                                        This action will be audit-logged with actor, timestamp, reason, note,
                                        IP, user-agent{bankRef ? ", and bank reference" : ""}.
                                        Once committed, the void is <b>permanent</b> — no unvoid path. Record a
                                        fresh settlement if you make a mistake.
                                    </div>

                                    <label className="flex items-start gap-2 cursor-pointer text-[12px] text-slate-800 border-t border-slate-100 pt-3">
                                        <input
                                            type="checkbox"
                                            checked={ack}
                                            onChange={(e) => setAck(e.target.checked)}
                                            className="mt-0.5 accent-rose-600"
                                            data-testid="void-payment-acknowledge"
                                        />
                                        <span>I have reviewed the impact above and confirm the void.</span>
                                    </label>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}

            <DialogFooter className="border-t border-slate-100 -mx-6 px-6 pt-4 mt-2">
                <FormDialogCancel disabled={busy} testId="void-payment-cancel-btn" />
                {step === 1 ? (
                    <Button
                        onClick={goToPreview}
                        disabled={busy || !step1Valid}
                        className="bg-rose-600 hover:bg-rose-500 text-white gap-1.5"
                        data-testid="void-payment-next-btn"
                    >
                        Review impact <ArrowRight size={14} />
                    </Button>
                ) : (
                    <>
                        <Button
                            onClick={() => setStep(1)}
                            disabled={busy}
                            variant="outline"
                            data-testid="void-payment-back-btn"
                        >
                            Back
                        </Button>
                        <Button
                            onClick={submit}
                            disabled={busy || !ack || isBlocked || previewBusy || !preview}
                            className="bg-rose-600 hover:bg-rose-500 text-white gap-1.5"
                            data-testid="void-payment-confirm-btn"
                        >
                            <Prohibit size={14} />
                            {busy ? "Voiding…" : "Confirm void payment"}
                        </Button>
                    </>
                )}
            </DialogFooter>
        </FormDialog>
    );
}
